import type { ReviewStatus } from '../types';
import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';
import type { OfflineReview } from './offlineDatabase.types';

type ReviewSyncStatus = OfflineReview['syncStatus'];

export interface ReviewSelectOptions {
  status?: ReviewStatus;
  syncStatus?: ReviewSyncStatus;
  environmentKey?: string;
  limit?: number;
  offset?: number;
}

export interface ReviewsByPropertySelectOptions {
  status?: ReviewStatus;
  environmentKey?: string;
  limit?: number;
}

export interface ReviewRowWriteValues {
  environmentKey: string;
  tempId: string | null;
  propertyId: number | undefined;
  templateId: number | undefined;
  reviewerId: number;
  status: ReviewStatus;
  startedAt: string;
  completedAt: string | null;
  submittedAt: string | null;
  notes: string | null;
  onDemandProjects: string | null;
  vendorNotes: string | null;
  address: string | null;
  memberName: string | null;
  memberEmail: string | null;
  homeManagerName: string | null;
  homeManagerEmail: string | null;
  metadata: string | null;
  syncStatus: ReviewSyncStatus;
  lastLocalMutationAt?: string | null;
  lastServerSeenAt?: string | null;
  localRevision?: number;
  serverRevision?: number;
  conflictStatus?: OfflineReview['conflictStatus'];
  updatedAt: string;
}

export interface CachedReviewRowWriteValues extends ReviewRowWriteValues {
  id: number;
  createdAt: string;
}

export interface ReviewRowUpdateValues extends ReviewRowWriteValues {
  id: number;
}

export const buildReviewByIdSelectStatement = (
  id: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM reviews WHERE id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [id, environmentKey] : [id],
});

export const buildReviewByTempIdSelectStatement = (
  tempId: string,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM reviews WHERE temp_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [tempId, environmentKey] : [tempId],
});

export const buildReviewsSelectStatement = (
  options: ReviewSelectOptions = {}
): OfflineDatabaseSqlStatement => {
  let sql = 'SELECT * FROM reviews WHERE 1=1';
  const params: unknown[] = [];

	  if (options.status) {
    sql += ' AND status = ?';
    params.push(options.status);
  }

	  if (options.syncStatus) {
    sql += ' AND sync_status = ?';
    params.push(options.syncStatus);
	  }

  if (options.environmentKey) {
    sql += ' AND environment_key = ?';
    params.push(options.environmentKey);
  }

  sql += ' ORDER BY created_at DESC';

  if (options.limit) {
    sql += ' LIMIT ?';
    params.push(options.limit);
  }

  if (options.offset) {
    sql += ' OFFSET ?';
    params.push(options.offset);
  }

  return { sql, params };
};

export const buildReviewsByPropertySelectStatement = (
  propertyId: number,
  options: ReviewsByPropertySelectOptions = {}
): OfflineDatabaseSqlStatement => {
  let sql = 'SELECT * FROM reviews WHERE property_id = ?';
  const params: unknown[] = [propertyId];

  if (options.status) {
    sql += ' AND status = ?';
    params.push(options.status);
  }

  if (options.environmentKey) {
    sql += ' AND environment_key = ?';
    params.push(options.environmentKey);
  }

  sql += ' ORDER BY completed_at DESC, updated_at DESC';

  if (options.limit) {
    sql += ' LIMIT ?';
    params.push(options.limit);
  }

  return { sql, params };
};

export const buildReviewAddressRowsSelectStatement = (environmentKey?: string): OfflineDatabaseSqlStatement => ({
  sql: `SELECT property_id, address FROM reviews
             WHERE address IS NOT NULL${environmentKey ? ' AND environment_key = ?' : ''}
             ORDER BY completed_at DESC, updated_at DESC`,
  params: environmentKey ? [environmentKey] : [],
});

export const buildRecentReviewAddressesSelectStatement = (
  limit: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT
              property_id,
              address,
              MAX(COALESCE(completed_at, updated_at)) as last_used,
              status
	             FROM reviews
	             WHERE address IS NOT NULL AND address != ''${environmentKey ? ' AND environment_key = ?' : ''}
	             GROUP BY property_id, address
             ORDER BY
               CASE WHEN status = 'completed' THEN 0 ELSE 1 END,
               last_used DESC
            LIMIT ?`,
  params: environmentKey ? [environmentKey, limit] : [limit],
});

export const buildReviewDeleteStatement = (
  id: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM reviews WHERE id = ?',
  params: [id],
});

export const buildCachedReviewInsertStatement = (
  values: CachedReviewRowWriteValues
): OfflineDatabaseSqlStatement => ({
	  sql: `INSERT OR IGNORE INTO reviews (
	          id, environment_key, temp_id, property_id, template_id, reviewer_id, status,
	          started_at, completed_at, submitted_at, notes, on_demand_projects,
	          vendor_notes, address, member_name, member_email, home_manager_name,
	          home_manager_email, metadata, sync_status, last_local_mutation_at,
	          last_server_seen_at, local_revision, server_revision, conflict_status,
	          created_at, updated_at
	        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	  params: [
	    values.id,
	    values.environmentKey,
	    values.tempId,
    values.propertyId,
    values.templateId,
    values.reviewerId,
    values.status,
    values.startedAt,
    values.completedAt,
    values.submittedAt,
    values.notes,
    values.onDemandProjects,
    values.vendorNotes,
    values.address,
    values.memberName,
    values.memberEmail,
    values.homeManagerName,
    values.homeManagerEmail,
    values.metadata,
    values.syncStatus,
    values.lastLocalMutationAt ?? null,
    values.lastServerSeenAt ?? null,
    values.localRevision ?? 0,
    values.serverRevision ?? 0,
    values.conflictStatus ?? 'none',
    values.createdAt,
    values.updatedAt,
  ],
});

export const buildReviewInsertStatement = (
  values: ReviewRowWriteValues
): OfflineDatabaseSqlStatement => ({
	  sql: `INSERT INTO reviews (
	          environment_key, temp_id, property_id, template_id, reviewer_id, status,
	          started_at, completed_at, submitted_at, notes, on_demand_projects,
	          vendor_notes, address, member_name, member_email, home_manager_name,
	          home_manager_email, metadata, sync_status, last_local_mutation_at,
	          last_server_seen_at, local_revision, server_revision, conflict_status,
	          created_at, updated_at
	        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	  params: [
	    values.environmentKey,
	    values.tempId,
    values.propertyId,
    values.templateId,
    values.reviewerId,
    values.status,
    values.startedAt,
    values.completedAt,
    values.submittedAt,
    values.notes,
    values.onDemandProjects,
    values.vendorNotes,
    values.address,
    values.memberName,
    values.memberEmail,
    values.homeManagerName,
    values.homeManagerEmail,
    values.metadata,
    values.syncStatus,
    values.lastLocalMutationAt ?? null,
    values.lastServerSeenAt ?? null,
    values.localRevision ?? 0,
    values.serverRevision ?? 0,
    values.conflictStatus ?? 'none',
    values.updatedAt,
    values.updatedAt,
  ],
});

export const buildReviewUpdateStatement = (
  values: ReviewRowUpdateValues
): OfflineDatabaseSqlStatement => ({
	  sql: `UPDATE reviews SET
	            environment_key = ?, temp_id = ?, property_id = ?, template_id = ?, reviewer_id = ?,
            status = ?, started_at = ?, completed_at = ?, submitted_at = ?,
            notes = ?, on_demand_projects = ?, vendor_notes = ?, address = ?,
            member_name = ?, member_email = ?, home_manager_name = ?, home_manager_email = ?,
            metadata = ?, sync_status = ?, last_local_mutation_at = ?,
            last_server_seen_at = ?, local_revision = ?, server_revision = ?,
	            conflict_status = ?, updated_at = ?
	           WHERE id = ? AND environment_key = ?`,
	  params: [
	    values.environmentKey,
	    values.tempId,
    values.propertyId,
    values.templateId,
    values.reviewerId,
    values.status,
    values.startedAt,
    values.completedAt,
    values.submittedAt,
    values.notes,
    values.onDemandProjects,
    values.vendorNotes,
    values.address,
    values.memberName,
    values.memberEmail,
    values.homeManagerName,
    values.homeManagerEmail,
    values.metadata,
    values.syncStatus,
    values.lastLocalMutationAt ?? null,
    values.lastServerSeenAt ?? null,
    values.localRevision ?? 0,
    values.serverRevision ?? 0,
    values.conflictStatus ?? 'none',
	    values.updatedAt,
	    values.id,
	    values.environmentKey,
	  ],
	});

export const buildReviewTemplateSnapshotUpsertStatement = (
  reviewId: number,
  templateData: string,
  timestamp: string
): OfflineDatabaseSqlStatement => ({
  sql: 'INSERT OR REPLACE INTO review_template_snapshots (review_id, template_data, cached_at, updated_at) VALUES (?, ?, ?, ?)',
  params: [reviewId, templateData, timestamp, timestamp],
});

export const buildReviewTemplateSnapshotSelectStatement = (
  reviewId: number
): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT template_data FROM review_template_snapshots WHERE review_id = ?',
  params: [reviewId],
});

export const buildReviewTemplateSnapshotDeleteStatement = (
  reviewId: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM review_template_snapshots WHERE review_id = ?',
  params: [reviewId],
});
